import { createBrowserRouter, redirect } from "react-router-dom";
import { lazy } from "react";
import { RequireAuth } from "@/components/RequireAuth";

// Lazy load all page components for better performance
const Home = lazy(() => import("@/pages/home"));
const DialsPage = lazy(() => import("@/pages/DialsPage"));
const SignIn = lazy(() => import("@/pages/sign-in"));
const SignUp = lazy(() => import("@/pages/sign-up"));
const User = lazy(() => import("@/pages/user"));
const DialPreview = lazy(() => import("@/pages/dev/DialPreview"));
const DockPreview = lazy(() => import("@/pages/dev/DockPreview"));

// The reading dossier is still beta, so it ships only where it is explicitly
// switched on: local dev, and any build that passes VITE_ENABLE_READING=true
// (staging, and the e2e bundle). Production omits the flag, which is the
// fail-safe direction — a forgotten flag hides the feature, never leaks it.
// Pairs with ENABLE_READING on the API; see agent-os/standards/global/monorepo.
const READING_ENABLED = import.meta.env.DEV || import.meta.env.VITE_ENABLE_READING === "true";

/**
 * The `/reading` route, or nothing. `import.meta.env.*` is replaced with a
 * literal at build time, so in a production build the guard folds to `false`
 * and everything below it is unreachable — the route disappears AND the
 * ReadingPage chunk is never emitted. That second half is why the `lazy()`
 * call lives in here rather than up with the others: Rollup can't prove
 * `lazy()` is side-effect-free, so a module-scope call survives DCE and drags
 * its dynamic import along. The `/dev/*` routes below demonstrate exactly that
 * — their chunks are in every production bundle (KI-26). Don't "tidy" this.
 */
function readingRoutes() {
	if (!READING_ENABLED) return [];
	const ReadingPage = lazy(() => import("@/pages/ReadingPage"));
	return [
		{
			path: "/reading",
			element: (
				<RequireAuth>
					<ReadingPage />
				</RequireAuth>
			),
		},
	];
}

// Root redirect from / to /home
const rootLoader = () => redirect("/home");

// Create router instance. Public: /home, /sign-in, /sign-up. Everything else
// is gated behind RequireAuth (mirrors the old Next middleware).
export const router = createBrowserRouter([
	{
		path: "/",
		loader: rootLoader,
	},
	{
		path: "/home",
		element: <Home />,
	},
	{
		path: "/dials",
		element: (
			<RequireAuth>
				<DialsPage />
			</RequireAuth>
		),
	},
	// Redirect the old PascalCase path so existing bookmarks/links still work.
	{
		path: "/DialsPage",
		loader: () => redirect("/dials"),
	},
	...readingRoutes(),
	{
		path: "/sign-in/*",
		element: <SignIn />,
	},
	{
		path: "/sign-up/*",
		element: <SignUp />,
	},
	{
		path: "/user/*",
		element: <User />,
	},
	// Dev-only preview harnesses for the dial renderer and the tool dock.
	...(import.meta.env.DEV
		? [
				{ path: "/dev/dial", element: <DialPreview /> },
				{ path: "/dev/dock", element: <DockPreview /> },
		  ]
		: []),
]);

export default router;
